import { clarityUtils, useClarityWorkflow } from 'api/clarityApi';
import { collaborateApi, useAddCommentMutation, useCreateNewTaskMutation, useUploadPredesignMutation } from 'api/collaborateApi';
import { detect } from 'detect-browser';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useStore } from 'react-redux';
import { toast } from 'sonner';
import { CollaborateTaskData, CreateTaskParams } from 'types/api/collaborate';
import { Comment, TaskAttributes } from 'types/api/tasks';
import { getPageUrl, isInChromeExtension, normalizeUrl } from 'utils/proxyUtils';
import { useAiPageReview } from '../../hooks/use-ai-page-review';
import { useAiSettings } from '../../hooks/use-ai-review-settings';
import { AtariMatesAnalysis } from '../../hooks/use-analyze-view-port';
import useElementContextAnalyzer, { EnhancedElementProperties } from '../../hooks/use-element-analyzer';
import useGetPageId from '../../hooks/use-get-pageid';
import useShowAI from '../../hooks/use-show-ai';
import { ScreenCapture } from '../../ScreenCapture';
import { screenshotStorage } from '../../services/screenshotStorage';
import { RootState, useAppDispatch, useAppSelector } from '../../store';
import { getSelectedSiteCollaborateInfo, getSelectedSiteId, getSelectedSiteInfo } from '../../store/siteInfoSlice';
import {
    addCommentToTask,
    creatingTask,
    getIsAIActive,
    getPendingTask,
    getSelectedTask,
    getSelectedTaskId,
    removeCommentFromTask,
    replaceComment,
    replaceTask,
    setAnimateScreenshot,
    setClaroPending,
    setSelectedTask,
    setSelectedTaskId
} from '../../store/tasksSlice';
import { setIsScreenshotInProgress, setViewportScreenshotId } from '../../store/uiSlice';
import { getLoggedIn, getTaskotifyUsers, getUser, setShowRegister } from '../../store/userSlice';
import { getNearestNode } from '../../utils/node';

type CreateTaskProps = {
    existingTask?: boolean;
    taskType?: 'Page' | 'General';
    taskId?: string | null;
    setFiles: React.Dispatch<React.SetStateAction<File[]>>;
    setComment: React.Dispatch<React.SetStateAction<string>>;
}

const useCurrentSelectedTask = () => {
    const store = useStore<RootState>();

    const getCurrentSelectedTask = useCallback(() => {
        return {
            selectedTask: getSelectedTask(store.getState()),
            selectedTaskId: getSelectedTaskId(store.getState()),
        }
    }, [store]);

    return getCurrentSelectedTask;
};

export const useCreateTask = ({ existingTask, taskType = 'Page', setFiles, setComment }: CreateTaskProps) => {
    // Default screenshot quality and preserveElements settings
    const quality = 0.95;
    const browser = detect();
    const dispatch = useAppDispatch();
    // const displayMode = useAppSelector(getDisplayMode);
    const [addComment, { isLoading: commentLoading, isSuccess: commentSuccess }] =
        useAddCommentMutation();
    const [createTask, { isLoading: taskLoading, isSuccess: taskSuccess }] =
        useCreateNewTaskMutation();
    const siteData = useAppSelector(getSelectedSiteInfo);
    const loggedIn = useAppSelector(getLoggedIn);
    const pendingTask = useAppSelector(getPendingTask);
    const siteId = useAppSelector(getSelectedSiteId);
    const userData = useAppSelector(getUser);
    const showAI = useShowAI();
    const collaborateInfo = useAppSelector(getSelectedSiteCollaborateInfo);
    const taskNotifyUsers = useAppSelector(getTaskotifyUsers);
    const isAIActive = useAppSelector(getIsAIActive)
    const { pageId } = useGetPageId();
    // subscribe to selectedTask
    const getCurrentSelectedTask = useCurrentSelectedTask();
    const { aiSettings } = useAiSettings();
    const screenshotCaptureRef = useRef<ScreenCapture | null>(null);
    const isMountedRef = useRef<boolean>(true);
    const activeClaroPendingTasksRef = useRef<Set<string>>(new Set());
    const {
        startClarityAnalysis,
    } = useClarityWorkflow();
    const elementAnalyzer = useElementContextAnalyzer();
    const [uploadPredesign] =
        useUploadPredesignMutation();
    const { uploadJson } = useAiPageReview();
    // const { analyzeViewport } = useViewportAnalyzer();


    // Initialize the ScreenCapture for task creation
    useEffect(() => {
        isMountedRef.current = true;
        screenshotCaptureRef.current = new ScreenCapture();
        const tasksSetAtMount = activeClaroPendingTasksRef.current;
        return () => {
            // Properly destroy the ScreenCapture instance and its worker
            if (screenshotCaptureRef.current) {
                screenshotCaptureRef.current.destroy();
                screenshotCaptureRef.current = null;
            }
            // Snapshot current pending tasks to avoid ref mutation during cleanup
            const tasksToClear = Array.from(tasksSetAtMount);
            if (tasksToClear.length > 0) {
                tasksToClear.forEach((taskId) => {
                    dispatch(setClaroPending({ task_id: taskId, pending: false }));
                });
                tasksSetAtMount.clear();
            }
            isMountedRef.current = false;
        };
    }, [dispatch]);

    const aiProjectTypes = useMemo(() => {
        if (!aiSettings?.settings?.project_types) return [];
        return aiSettings.settings.project_types.map((type: { value: string, label: string }) => type.label) || [];
    }, [aiSettings?.settings?.project_types])


    // Helper function to create optimistic updates for task creation
    const createOptimisticTaskUpdates = (data: CreateTaskParams) => {

        const timestamp = new Date().toISOString();

        // Create optimistic comment
        const optimisticComment: Comment = {
            norm_id: userData?.normal_id || 0,
            id: `OPTIMISTIC-${Date.now()}`, // Temporary ID
            comments: data.task_comment_message,
            user_id: data.task_config_author_id,
            user_normal_id: userData?.normal_id || 0,
            user_name: userData?.name || '',
            is_deleted: false,
            is_edited: false,
            updated_at: timestamp,
            is_note: data.is_note,
            days: 'posting...',
            comment_date: new Date().toISOString(),
            comment_type: 'normal_text',
            login_user_name: userData?.name || '',
            from_name: '',
            to: [],
            bcc: [],
            cc: [],
            subject: '',
            from_email: '',
            attachments: [],
            email: '',
            avatar: userData?.avatar ?? '',
            is_guest: false,
            ai_chat: data.ai_chat ?? false,
            files:
                data.files?.map((file) => ({
                    id: Date.now(),
                    user_id: Number(data.task_config_author_id),
                    comment_id: Date.now(),
                    task_id: Number(data.task_id),
                    site_id: Number(data.site_id),
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    url: URL.createObjectURL(file),
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                })) || [],
            ...(isAIActive && { settings: aiSettings?.settings })
        };

        const optimisticTaskData: CollaborateTaskData = {
            collaborators: [],
            height: data.viewport_height,
            width: data.viewport_width,
            id: data.task_comment_id,
            latest_comment: {
                author: {
                    avatar: userData?.avatar ?? '',
                    email: userData?.email ?? '',
                    id: userData?.id,
                    user_name: userData?.name || '',
                },
                comments: data.task_comment_message,
                comment_type: 'normal_text',
                days: 'posting...',
                id: crypto.randomUUID(),
            },
            notify_wp_feedback_users: [],
            page_approved_at: new Date().toISOString(),
            task_id: data.task_id.replace('PENDING', 'OPTIMISTIC'),
            task_title: data.task_title,
            task_page_title: data.task_page_title,
            task_comment_id: data.task_comment_id,
            task_comment_message: data.task_comment_message,
            task_created_by: data.task_config_author_name,
            task_created_time: new Date().toISOString(),
            task_created_time_ago: 'posting...',
            task_type: ['Page'],
            is_complete: false,
            is_internal: false,
            is_unread: true,
            task_status: data.task_status,
            task_priority: data.task_priority,
            page_url: data.task_page_url,
            task_page_url: data.task_page_url,
            task_elementX: data.viewport_width,
            task_elementY: data.viewport_height,
            task_element_path: data.task_element_path,
            graphic_id: null,
            task_tags: [],
            is_approved: 0,
            atarim_task_screenshot: data.screenshot_base64 || '',
            is_pinned: false,
            wpf_task_screenshot: data.screenshot_base64 || '',
            task_position: [],
            is_guest: false,
        };

        // Create optimistic task attributes for the detailed view
        const optimisticTaskAttributes: TaskAttributes = {
            wpf_task_screenshot: data.screenshot_base64 || '',
            id: data.task_id.replace('PENDING', 'OPTIMISTIC'),
            task_id: data.task_id.replace('PENDING', 'OPTIMISTIC'),
            task_comment_id: data.task_comment_id,
            comments: [optimisticComment],
            admin_user_id: Number(data.task_config_author_id),
            additional_information: [],
            task_status: [
                {
                    label: data.task_status,
                    value: data.task_status,
                    selected: true,
                },
            ],
            task_urgency: [
                {
                    label: data.task_priority,
                    value: data.task_priority,
                    selected: true,
                },
            ],
            notify_wp_feedback_users: [],
            collaborators: [],
            spent_time: 0,
            task_page_url: data.task_page_url,
            page_url: data.task_page_url,
            estimated_time: [],
            task_elementX: data.viewport_width,
            task_elementY: data.viewport_height,
            task_element_path: data.task_element_path,
            task_page_title: data.task_page_title,
            site_id: data.site_id,
            is_collaborate: true,
            is_unread: true,
            is_pinned: false,
            workspace_id: 0,
            task_type: [],
            task_title: data.task_title,
            wpf_task_url: data.task_page_url,
            task_tags: [],
            is_internal: 0,
        };

        return {
            optimisticComment,
            optimisticTaskData,
            optimisticTaskAttributes,
        };
    };

    const applyOptimisticTaskUpdates = async (data: CreateTaskParams) => {
        const { optimisticTaskData, optimisticTaskAttributes } = createOptimisticTaskUpdates(data);
        dispatch(replaceTask({ task_id: data.task_id, task: optimisticTaskData }));
        dispatch(setSelectedTask(optimisticTaskAttributes));
        dispatch(setSelectedTaskId(optimisticTaskData.task_id));
        await new Promise(resolve => setTimeout(resolve, 0));
        return {
            optimisticTaskData,
            optimisticTaskAttributes,
        }
    };

    // Capture screenshot immediately before task creation
    const captureTaskScreenshot = async (): Promise<string> => {
        if (!screenshotCaptureRef.current || !pendingTask?.task_element_path) {
            return '';
        }

        try {
            dispatch(setIsScreenshotInProgress(true));

            // Try to find the target element using the stored path
            const targetElement = await getNearestNode(pendingTask?.task_element_path) || document.body;

            // Use the ScreenCapture to capture the screenshot
            const screenshot = await screenshotCaptureRef.current.capture({
                quality,
                preserveElements: true,
                useSnapdom: true,
                useChromeApi: isInChromeExtension(),
                selectedElement: targetElement as HTMLElement | null,
                elementPath: pendingTask?.task_element_path,
                borderStyle: {
                    color: 'red',
                    width: 3,
                    style: 'dashed'
                }
            });
            return screenshot as string;
        } catch (error) {
            console.error('Error capturing task screenshot:', error);
            return '';
        } finally {
            dispatch(setIsScreenshotInProgress(false));
        }
    };


    const newTask = async (note: boolean, comment: string, files: File[]) => {
        if (!browser || !siteData || !userData) return;

        try {
            // Set animation state to false before capturing
            dispatch(setAnimateScreenshot(false));
            // Capture the screenshot right before creating the task


            let screenshot: string | undefined = undefined;
            if (pendingTask?.task_element_path && isInChromeExtension()) {
                screenshot = await captureTaskScreenshot();
            }
            requestAnimationFrame(async () => {
                dispatch(setAnimateScreenshot(true));

                // Store the screenshot in IndexedDB
                if (screenshot) {
                    const screenshotId = `viewport_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                    await screenshotStorage.storeScreenshot(
                        screenshotId,
                        screenshot,
                        'viewport'
                    );
                    dispatch(setViewportScreenshotId(screenshotId));
                }
            });

            let scrollTo = 0;
            if (pendingTask?.task_element_path) {
                const element = await getNearestNode(pendingTask?.task_element_path);
                const rect = element?.getBoundingClientRect();
                if (rect) {
                    const elementY = Math.round(rect?.top + window.scrollY);
                    const elementHeight = Math.round(rect?.height || 0);
                    const viewportHeight = window.innerHeight;
                    scrollTo = elementY + elementHeight / 2 - viewportHeight / 2;
                }
            }
            const task_url = getPageUrl();

            // Perform AI analysis if active
            let aiAnalysisResult: AIAnalysisResult | null = null;
            if (isAIActive && taskType !== 'General') {
                aiAnalysisResult = await performAIAnalysisAndUpload({
                    taskId: pendingTask?.task_id,
                    elementPath: pendingTask?.task_element_path,
                    fileNamePrefix: 'ai-new-task'
                });
            }
            const aiNewSettings = {
                ...aiSettings?.settings,
                project_types: aiProjectTypes,
            }

            const newTaskData: CreateTaskParams = {
                avatar: userData.avatar ?? '',
                task_id: pendingTask?.task_id ?? '',
                comments: [
                    {
                        comment_type: 'normal_text',
                        comments: comment,
                        days: '0s',
                        files,
                        is_deleted: false,
                        is_edited: false,
                        is_guest: false,
                        is_note: note ? 1 : 0,
                        user_id: userData.id,
                        user_name: userData.name,
                    },
                ],
                files,
                screenshot_base64: screenshot ?? undefined,
                guest_user_id: collaborateInfo?.result?.role === 'Guest' ? collaborateInfo?.result.guest_user_id : '',
                id: 99999,
                internal: 0,
                is_note: 0,
                scroll_to: scrollTo,
                site_id: siteId ?? '',
                task_comment_id: pendingTask?.task_comment_id ?? 0,
                task_comment_message: comment,
                task_config_author_browser: browser.name,
                task_config_author_browserVersion: browser.version ?? '0',
                task_config_author_id: collaborateInfo?.result?.role !== 'Guest' ? userData.id : '',
                task_config_author_name: userData.name,
                task_config_author_resX: screen.width,
                task_config_author_resY: screen.height,
                task_element_path: pendingTask?.task_element_path ?? 'body',
                task_notify_users: taskNotifyUsers?.join(',') ?? userData.id,
                task_page_title: siteData.name,
                task_page_url: normalizeUrl(task_url ?? ''),
                task_priority: pendingTask?.task_priority ?? 'low',
                task_status: pendingTask?.task_status ?? 'open',
                task_title: comment,
                task_type: taskType,
                viewport_height: window.innerHeight,
                viewport_width: window.innerWidth,
                ai_chat: isAIActive,
                current_page_id: pageId ?? '',
                ...(isAIActive && { settings: aiNewSettings }),
                ... ((isAIActive && aiAnalysisResult) && { html_json: aiAnalysisResult.htmlJsonKey })
            }

            // Apply optimistic updates before making the request
            const { optimisticTaskData } = await applyOptimisticTaskUpdates(newTaskData);

            try {
                const taskCreationResponse = await createTask(newTaskData).unwrap();
                setComment('');
                setFiles([]);

                if (taskCreationResponse) {

                    // Replace the optimistic task in the store
                    dispatch(replaceTask({ task_id: optimisticTaskData.task_id, task: taskCreationResponse.result as CollaborateTaskData }));

                    // Only update the selected task ID if the user is still viewing the optimistic task
                    // This prevents forcing the user back to a task they've navigated away from
                    if (getCurrentSelectedTask().selectedTaskId === optimisticTaskData.task_id) {
                        dispatch(setSelectedTaskId(taskCreationResponse.result.task_id));
                    }

                    // if (displayMode === 'desktop') {
                    //     if (taskCreationResponse.result.task_type.some(type => type !== 'General')) {
                    //         dispatch(setCreateModalOpen(task_id));
                    //         if (taskCreationResponse.result.task_element_path) {
                    //             await scrollToTaskElement(taskCreationResponse.result.task_element_path);
                    //         }
                    //     } else {
                    //         dispatch(setGeneralTaskModalOpen(task_id));
                    //     }
                    // } else {
                    //     // Send to iframe
                    //     window.parent.postMessage({
                    //         type: 'OPEN_TASK',
                    //         taskId: task_id,
                    //     }, '*');
                    // }

                    // Start clarity analysis if element path is available
                    if (pendingTask?.task_element_path && !isAIActive && showAI) {
                        if (!screenshot) {
                            screenshot = await captureTaskScreenshot();
                        }
                        await sendClarityAnalysis(
                            taskCreationResponse.result.task_id,
                            taskCreationResponse.result.latest_comment?.id,
                            taskCreationResponse.result.latest_comment?.comments,
                            pendingTask?.task_element_path,
                            screenshot ?? taskCreationResponse.result.wpf_task_screenshot ?? undefined
                        );
                    }

                    // The cache will be invalidated by the mutation, which will fetch fresh data
                    // and replace our optimistic updates with real server data
                }
            } catch (error) {
                console.error('Error creating task', error);
                // Undo optimistic updates if the request fails
                // TODO: Undo optimistic updates
                // Optionally, you can dispatch an error action or show a toast notification
                dispatch(creatingTask(false));
                toast.error('Error creating task');
            }
        } catch (error) {
            console.error('Error creating task', error);
            dispatch(creatingTask(false));
        }
    };

    const newComment = async (note: boolean, comment: string, files: File[]) => {

        const taskId = getCurrentSelectedTask().selectedTaskId;
        if (!taskId || !userData) return;

        let optimisticComment: Comment | null = null;

        try {
            // Perform AI analysis if active
            // TODO: Uncomment this when we have a way to use the html_json efficiently
            const aiAnalysisResult = await performAIAnalysisAndUpload({
                taskId,
                fileNamePrefix: 'task-comment'
            });
            optimisticComment = {
                norm_id: userData?.normal_id || 0,
                id: `OPTIMISTIC-${Date.now()}`, // Temporary ID
                comments: comment,
                user_id: userData.id,
                user_normal_id: userData.normal_id || 0,
                user_name: userData.name,
                is_deleted: false,
                is_edited: false,
                updated_at: new Date().toISOString(),
                is_note: note ? 1 : 0,
                days: 'posting...',
                comment_date: new Date().toISOString(),
                comment_type: 'normal_text',
                login_user_name: userData.name,
                from_name: '',
                to: [],
                bcc: [],
                cc: [],
                subject: '',
                from_email: '',
                attachments: [],
                email: '',
                avatar: userData.avatar ?? '',
                is_guest: false,
                ai_chat: isAIActive,
                files: files.length > 0 ?
                    files.map(file => ({
                        id: Date.now(),
                        user_id: Number(userData.id),
                        comment_id: Date.now(),
                        task_id: Number(taskId),
                        site_id: Number(siteId),
                        name: file.name,
                        type: file.type,
                        size: file.size,
                        url: URL.createObjectURL(file),
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                    })) : []
            };

            // Optimistically update the cache
            dispatch(addCommentToTask({ task_id: taskId, comment: optimisticComment }));

            const response = await addComment({
                comment_content: comment,
                comment_type: 'normal_text',
                complete_task: false,
                files: files,
                guest_author_name: userData.name,
                guest_user_id: collaborateInfo?.result?.role === 'Guest' ? collaborateInfo?.result.guest_user_id : '',
                is_note: note ? 1 : 0,
                task_id: taskId,
                user_id: collaborateInfo?.result?.role !== 'Guest' ? userData.id : '',
                ai_chat: isAIActive,
                ...(isAIActive && { settings: aiSettings?.settings }),
                // TODO: Uncomment this when we have a way to use the html_json efficiently
                ... ((isAIActive && aiAnalysisResult && taskType !== 'General') && { html_json: aiAnalysisResult.htmlJsonKey })
            }).unwrap();
            setComment('');
            setFiles([]);

            // Update the optimistic comment with the real data from server
            if (response.status && response.result) {
                dispatch(replaceComment({ task_id: taskId, comment: response.result, comment_id: optimisticComment.id }));
                dispatch(collaborateApi.util.invalidateTags([{ type: 'CollaborateTasks', id: taskId }]));
            }

            // Only clear after successful response
            dispatch(creatingTask(false));
        } catch (error) {
            console.error('Error adding comment', error);
            // Remove the optimistic comment on error
            if (optimisticComment) {
                dispatch(removeCommentFromTask({ task_id: taskId, comment_id: optimisticComment.id }));
            }
            dispatch(creatingTask(false));
            toast.error('There was an error adding your comment');
        }
    };

    const handleCreate = (note: boolean, comment: string, files: File[]) => {
        if (!loggedIn) {
            dispatch(setShowRegister(true));
            return;
        }
        if (existingTask) {
            newComment(note, comment, files);
        } else {
            dispatch(creatingTask(true));
            newTask(note, comment, files);
        }
    };

    const sendClarityAnalysis = async (
        taskId: string,
        commentId: string,
        feedback: string,
        elementPath?: string,
        screenshot?: string
    ) => {
        try {
            if (!commentId || !feedback) return;

            let elementProperties: EnhancedElementProperties | undefined = undefined;
            const elementScreenshot: string | undefined = undefined;

            if (elementPath) {
                const targetElement = await getNearestNode(elementPath);
                const analysisData = await elementAnalyzer.analyzeElementForClarity(elementPath);
                if (analysisData) {
                    elementProperties = elementAnalyzer.formatForWorker(analysisData);
                } else if (targetElement) {
                    elementProperties = {
                        className: targetElement.className,
                        id: targetElement.id,
                        tagName: targetElement.tagName,
                        position: {
                            x: targetElement.getBoundingClientRect().left,
                            y: targetElement.getBoundingClientRect().top,
                            width: targetElement.getBoundingClientRect().width,
                            height: targetElement.getBoundingClientRect().height,
                        },
                        textContent: targetElement.textContent || '',
                        computedStyles: window.getComputedStyle(targetElement),
                    };
                }
            }

            if (taskId) {
                dispatch(setClaroPending({ task_id: taskId, pending: true }));
                activeClaroPendingTasksRef.current.add(taskId);
            }
            await startClarityAnalysis({
                commentId,
                feedback,
                elementProperties,
                screenshot,
                elementScreenshot,
                cacheKey: clarityUtils.generateCacheKey(commentId, feedback),
                userName: userData?.name
            })
                .then(() => {
                    if (taskId) {
                        if (isMountedRef.current) {
                            dispatch(setClaroPending({ task_id: taskId, pending: false }));
                        }
                        activeClaroPendingTasksRef.current.delete(taskId);
                    }
                })
                .catch(() => {
                    if (taskId) {
                        if (isMountedRef.current) {
                            dispatch(setClaroPending({ task_id: taskId, pending: false }));
                        }
                        activeClaroPendingTasksRef.current.delete(taskId);
                    }
                });

        } catch (error) {
            console.error('Error sending clarity analysis:', error);
        }
    }

    interface AIAnalysisOptions {
        taskId?: string;
        elementPath?: string;
        fileNamePrefix?: string;
    }

    interface AIAnalysisResult {
        uploadResponse: unknown;
        elementProperties: EnhancedElementProperties | AtariMatesAnalysis;
        htmlJsonKey: string;
    }

    // const handleAnalyze = useCallback(async () => {
    //     try {
    //         const viewportAnalysis = analyzeViewport({ analyzeFullDocument: true });
    //         return convertToAtariMatesFormat(viewportAnalysis);
    //     } catch (error) {
    //         console.error('Analysis failed:', error);
    //         throw error;
    //     }
    // }, [analyzeViewport]);

    const performAIAnalysisAndUpload = async (options: AIAnalysisOptions = {}): Promise<AIAnalysisResult | null> => {
        if (!isAIActive || taskType === 'General') {
            return null;
        }

        try {
            const {
                taskId = getCurrentSelectedTask().selectedTaskId || 'unknown',
                elementPath = getCurrentSelectedTask()?.selectedTask?.task_element_path ?? '',
                fileNamePrefix = 'ai-chat'
            } = options;

            const uploadResponse = await uploadPredesign({
                files: [{ name: `${fileNamePrefix}-${taskId}-${Date.now()}.json` }],
            }).unwrap();
            // const fullDocAtariMatesData = await handleAnalyze();

            // let elementProperties: EnhancedElementProperties | AtariMatesAnalysis;

            // if (isGeneralTask) {
            //     elementProperties = fullDocAtariMatesData;
            // } else {
            // For regular tasks, analyze the specific element
            const analysisData = await elementAnalyzer.analyzeElementForClarity(elementPath);

            if (!analysisData) {
                throw new Error('Element analysis failed');
            }

            const elementProperties = elementAnalyzer.formatForWorker(analysisData);
            // }

            // Step 4: Upload JSON data
            await uploadJson(
                elementProperties,
                uploadResponse.result[0].url
            );

            return {
                uploadResponse,
                elementProperties,
                htmlJsonKey: uploadResponse.result[0].key
            };

        } catch (error) {
            console.error('AI analysis and upload failed:', error);
            throw error;
        }
    };

    return {
        handleCreate,
        isLoading: commentLoading || taskLoading,
        isSuccess: commentSuccess || taskSuccess,
    };
};